import { SxProps, Theme } from '@mui/material';

// Neumorphism color palette
export const neuColors = {
    background: '#e0e5ec',
    backgroundDark: '#d1d9e6',
    backgroundLight: '#f0f4f8',
    text: '#4a5568',
    textLight: '#718096',
    primary: '#084608',
    primaryLight: '#0a5e0a',
    white: '#ffffff',
};

// Neumorphic shadows for raised elements
export const neuShadowRaised = `
  8px 8px 16px rgba(163, 177, 198, 0.6),
  -8px -8px 16px rgba(255, 255, 255, 0.5)
`;

// Neumorphic shadows for pressed/inset elements
export const neuShadowInset = `
  inset 4px 4px 8px rgba(163, 177, 198, 0.5),
  inset -4px -4px 8px rgba(255, 255, 255, 0.5)
`;

// Subtle neumorphic shadow for cards
export const neuShadowCard = `
  12px 12px 24px rgba(163, 177, 198, 0.4),
  -12px -12px 24px rgba(255, 255, 255, 0.6)
`;

// Hover effect for neumorphic elements
export const neuShadowHover = `
  6px 6px 12px rgba(163, 177, 198, 0.6),
  -6px -6px 12px rgba(255, 255, 255, 0.5)
`;

// Active/pressed effect
export const neuShadowActive = `
  inset 2px 2px 4px rgba(163, 177, 198, 0.5),
  inset -2px -2px 4px rgba(255, 255, 255, 0.4)
`;

// Neumorphic button style
export const neuButton: SxProps<Theme> = {
    background: neuColors.background,
    boxShadow: neuShadowRaised,
    border: 'none',
    borderRadius: 3,
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: neuShadowHover,
        transform: 'translateY(-1px)',
    },
    '&:active': {
        boxShadow: neuShadowActive,
        transform: 'translateY(0)',
    },
};

// Neumorphic card style
export const neuCard: SxProps<Theme> = {
    background: neuColors.background,
    boxShadow: neuShadowCard,
    borderRadius: 4,
    border: 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: neuShadowHover,
        transform: 'translateY(-2px)',
    },
};

// Neumorphic input style
export const neuInput: SxProps<Theme> = {
    background: neuColors.background,
    boxShadow: neuShadowInset,
    borderRadius: 3,
    border: 'none',
    padding: '12px 16px',
    transition: 'all 0.3s ease',
    '&:focus': {
        boxShadow: `
      inset 3px 3px 6px rgba(163, 177, 198, 0.4),
      inset -3px -3px 6px rgba(255, 255, 255, 0.4),
      0 0 0 2px ${neuColors.primary}33
    `,
        outline: 'none',
    },
};

// Neumorphic container style
export const neuContainer: SxProps<Theme> = {
    background: neuColors.background,
    minHeight: '100vh',
    padding: 3,
};

// Neumorphic stats card
export const neuStatsCard: SxProps<Theme> = {
    background: `linear-gradient(145deg, ${neuColors.backgroundLight}, ${neuColors.background})`,
    boxShadow: neuShadowCard,
    borderRadius: 3,
    padding: 3,
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: neuShadowHover,
        transform: 'translateY(-3px)',
    },
};

// Neumorphic icon button
export const neuIconButton: SxProps<Theme> = {
    background: neuColors.background,
    boxShadow: neuShadowRaised,
    borderRadius: 2,
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: neuShadowHover,
        background: `linear-gradient(145deg, ${neuColors.backgroundLight}, ${neuColors.backgroundDark})`,
    },
    '&:active': {
        boxShadow: neuShadowActive,
    },
};

// Active/selected state for buttons
export const neuButtonActive: SxProps<Theme> = {
    background: `linear-gradient(145deg, ${neuColors.primary}, ${neuColors.primaryLight})`,
    boxShadow: neuShadowInset,
    color: '#ffffff',
    '&:hover': {
        background: `linear-gradient(145deg, ${neuColors.primaryLight}, ${neuColors.primary})`,
        boxShadow: neuShadowInset,
    },
};

export default {
    neuColors,
    neuShadowRaised,
    neuShadowInset,
    neuShadowCard,
    neuShadowHover,
    neuShadowActive,
    neuButton,
    neuCard,
    neuInput,
    neuContainer,
    neuStatsCard,
    neuIconButton,
    neuButtonActive,
};
